import React, { useState, useEffect, useRef } from 'react';
import { getAllDeals, deleteDeal, createDeal, updateDeal, transcribeAudio, getEmployees, assignEmployeesToDeal, getDealAssignments } from '../utils/api';
import DealModal from './DealModal';
import { parseDealText, formatParsedDeal } from '../utils/dealParser';
import { ar, en } from '../utils/translations';
// React Icons - Colorful icons
import { 
  FaMicrophone, 
  FaStop, 
  FaEdit, 
  FaTh, 
  FaTable, 
  FaMapMarkerAlt, 
  FaMoneyBillWave, 
  FaCalendar,
  FaClock,
  FaPlus,
  FaUserFriends,
  FaCheckCircle
} from 'react-icons/fa';

const Deals = ({ language = 'ar', userRole }) => {
  const t = language === 'ar' ? ar : en;
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dealTypeFilter, setDealTypeFilter] = useState('');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [employees, setEmployees] = useState([]);
  const [dealAssignments, setDealAssignments] = useState({}); // { dealId: [assignments] }
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assigningDeal, setAssigningDeal] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  
  const isOwnerOrManager = userRole === 'owner' || userRole === 'manager';
  
  // ChatGPT-style input
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedPreview, setParsedPreview] = useState(null);
  const [recordingError, setRecordingError] = useState(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const lastInputTextRef = useRef('');
  const [progressEdits, setProgressEdits] = useState({});

  const getDealStatusMeta = (status) => {
    switch (status) {
      case 'completed':
        return { label: t.completed || 'Completed', background: '#d1fae5', color: '#065f46' };
      case 'in_progress':
        return { label: t.inProgress || t.in_progress || 'In Progress', background: '#dbeafe', color: '#1e40af' };
      default:
        return { label: t.pending || 'Pending', background: '#fef3c7', color: '#92400e' };
    }
  };

  const clampProgress = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const num = Number(value);
    if (Number.isNaN(num)) return 0;
    return Math.max(0, Math.min(100, num));
  };

  const getProgressValue = (deal) => {
    if (progressEdits[deal.id] === undefined) {
      return deal.progress || 0;
    }
    return progressEdits[deal.id];
  };

  const handleProgressChange = (dealId, value) => {
    setProgressEdits(prev => ({
      ...prev,
      [dealId]: clampProgress(value)
    }));
  };

  const handleProgressSave = async (deal) => {
    const newProgress = getProgressValue(deal);
    if (newProgress === (deal.progress || 0)) return;
    try {
      await updateDeal(deal.id, { progress: newProgress });
      await loadDeals();
    } catch (error) {
      console.error('Error updating deal progress:', error);
      alert(language === 'ar' ? 'فشل تحديث نسبة التقدم' : 'Failed to update progress');
    }
  };

  const handleQuickProgressSet = async (deal, value) => {
    const newProgress = clampProgress(value);
    setProgressEdits(prev => ({ ...prev, [deal.id]: newProgress }));
    try {
      await updateDeal(deal.id, { progress: newProgress });
      await loadDeals();
    } catch (error) {
      console.error('Error updating deal progress:', error);
      alert(language === 'ar' ? 'فشل تحديث نسبة التقدم' : 'Failed to update progress');
    }
  };

  // Cleanup media stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Load deals when filters change
  useEffect(() => {
    loadDeals();
  }, [searchTerm, statusFilter, dealTypeFilter, propertyTypeFilter]);

  // Load employees if owner/manager
  useEffect(() => {
    if (isOwnerOrManager) {
      loadEmployees();
    }
  }, [isOwnerOrManager]);

  // Load assignments for all deals
  useEffect(() => {
    console.log('useEffect for assignments triggered:', { isOwnerOrManager, dealsLength: deals.length });
    if (isOwnerOrManager && deals.length > 0) {
      console.log('Loading all assignments for', deals.length, 'deals');
      loadAllAssignments();
    }
  }, [deals, isOwnerOrManager]);

  const loadEmployees = async () => {
    try {
      const response = await getEmployees();
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadAllAssignments = async () => {
    console.log('🔄 loadAllAssignments called for', deals.length, 'deals');
    const assignments = {};
    for (const deal of deals) {
      try {
        const response = await getDealAssignments(deal.id);
        console.log(`✅ Assignments for deal ${deal.id}:`, response.data);
        assignments[deal.id] = response.data || [];
      } catch (error) {
        console.error(`❌ Error loading assignments for deal ${deal.id}:`, error);
        assignments[deal.id] = [];
      }
    }
    console.log('✅ All assignments loaded:', assignments);
    setDealAssignments(assignments);
    console.log('✅ State updated with assignments');
  };

  const handleAssignClick = (deal) => {
    setAssigningDeal(deal);
    const currentAssignments = dealAssignments[deal.id] || [];
    setSelectedEmployees(currentAssignments.map(a => a.employee_id));
    setShowAssignmentModal(true);
  };

  const handleAssignSubmit = async () => {
    if (!assigningDeal) return;
    
    if (!selectedEmployees || selectedEmployees.length === 0) {
      alert(language === 'ar' ? 'يرجى اختيار موظف واحد على الأقل' : 'Please select at least one employee');
      return;
    }
    
    // Ensure selectedEmployees is an array of integers
    const employeeIds = Array.isArray(selectedEmployees) 
      ? selectedEmployees.map(id => parseInt(id)).filter(id => !isNaN(id))
      : [];
    
    console.log('Assigning employees to deal:', {
      dealId: assigningDeal.id,
      originalSelectedEmployees: selectedEmployees,
      processedEmployeeIds: employeeIds,
      employeesType: typeof selectedEmployees,
      isArray: Array.isArray(selectedEmployees)
    });
    
    if (employeeIds.length === 0) {
      alert(language === 'ar' ? 'يرجى اختيار موظف واحد على الأقل' : 'Please select at least one employee');
      return;
    }
    
    try {
      await assignEmployeesToDeal(assigningDeal.id, employeeIds);
      alert(language === 'ar' ? 'تم تعيين الموظفين بنجاح' : 'Employees assigned successfully');
      setShowAssignmentModal(false);
      
      // Reload assignments for the specific deal immediately
      try {
        const assignResponse = await getDealAssignments(assigningDeal.id);
        console.log('✅ Reloaded assignments for deal:', assigningDeal.id, assignResponse.data);
        setDealAssignments(prev => {
          const updated = {
            ...prev,
            [assigningDeal.id]: assignResponse.data || []
          };
          console.log('✅ Updated dealAssignments state:', updated);
          return updated;
        });
      } catch (assignError) {
        console.error('❌ Error reloading assignments:', assignError);
      }
      
      // Also reload all assignments after a short delay to ensure DB is updated
      setTimeout(() => {
        console.log('🔄 Reloading all assignments after delay...');
        loadAllAssignments();
      }, 500);
    } catch (error) {
      console.error('Error assigning employees:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      const errorMsg = error.response?.data?.error || error.message || (language === 'ar' ? 'فشل تعيين الموظفين' : 'Failed to assign employees');
      alert(errorMsg);
    }
  };

  // Parse input text as user types (debounced)
  useEffect(() => {
    if (inputText.trim()) {
      const timer = setTimeout(() => {
        const parsed = parseDealText(inputText);
        setParsedPreview(formatParsedDeal(parsed));
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setParsedPreview(null);
    }
  }, [inputText]);

  const loadDeals = async () => {
    try {
      setLoading(true);
      const response = await getAllDeals(
        searchTerm || null,
        statusFilter || null,
        dealTypeFilter || null,
        propertyTypeFilter || null
      );
      // Calculate priority for deals that don't have it, then sort
      const dealsWithPriority = (response.data || []).map(deal => {
        // If priority not set, calculate it
        if (!deal.priority_level || !deal.priority_score) {
          const calculated = calculateDealPriority(deal);
          deal.priority_level = calculated.level;
          deal.priority_score = calculated.score;
        }
        return deal;
      });
      
      // Sort deals by priority (critical > high > medium > low), then by score
      const sortedDeals = dealsWithPriority.sort((a, b) => {
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        const aPriority = priorityOrder[a.priority_level] || 0;
        const bPriority = priorityOrder[b.priority_level] || 0;
        
        if (aPriority !== bPriority) {
          return bPriority - aPriority; // Higher priority first
        }
        
        // If same priority level, sort by score
        const aScore = a.priority_score || 0;
        const bScore = b.priority_score || 0;
        return bScore - aScore; // Higher score first
      });
      setDeals(sortedDeals);
    } catch (error) {
      console.error('Error loading deals:', error);
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        alert(t.cannotConnect + '\n\n' + t.runCommand);
      } else {
        alert(t.failedToLoadDeals || 'Failed to load deals');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t.deleteDealConfirm || 'Are you sure you want to delete this deal?')) {
      try {
        await deleteDeal(id);
        loadDeals();
      } catch (error) {
        console.error('Error deleting deal:', error);
        alert(t.failedToDelete || 'Failed to delete deal');
      }
    }
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingDeal(null);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingDeal(null);
    loadDeals();
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return price;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US');
  };

  const translatePropertyType = (type) => {
    if (!type) return '-';
    const types = {
      apartment: language === 'ar' ? 'شقة' : 'Apartment',
      villa: language === 'ar' ? 'فيلا' : 'Villa',
      land: language === 'ar' ? 'أرض' : 'Land',
      commercial: language === 'ar' ? 'تجاري' : 'Commercial',
      office: language === 'ar' ? 'مكتب' : 'Office',
      other: language === 'ar' ? 'أخرى' : 'Other'
    };
    return types[type] || type;
  };

  const translateDealType = (type) => {
    if (!type) return '-';
    return type === 'sale' ? (language === 'ar' ? 'بيع' : 'Sale') : (language === 'ar' ? 'إيجار' : 'Rent');
  };

  // Calculate priority if not set (same logic as backend)
  const calculateDealPriority = (deal) => {
    let score = 0;
    
    // Value score (40% weight)
    let valueScore = 50;
    if (deal.price) {
      const priceStr = typeof deal.price === 'string' ? deal.price : String(deal.price || '');
      const priceNum = parseFloat(priceStr.replace(/[^0-9.]/g, '')) || 0;
      valueScore = Math.min(100, (priceNum / 10000) * 10);
    }
    score += valueScore * 0.4;
    
    // Urgency score (40% weight)
    const targetDate = deal.due_date || deal.deal_date;
    let urgencyScore = 50;
    if (targetDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(targetDate);
      due.setHours(0, 0, 0, 0);
      const diffTime = due - today;
      const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (daysUntil < 0) urgencyScore = 100;
      else if (daysUntil === 0) urgencyScore = 90;
      else if (daysUntil <= 3) urgencyScore = 80;
      else if (daysUntil <= 7) urgencyScore = 60;
      else urgencyScore = 40;
    }
    score += urgencyScore * 0.4;
    
    // Progress score (20% weight)
    score += (deal.progress || 0) * 0.2;
    
    // Determine level
    let level = 'medium';
    if (score >= 80) level = 'critical';
    else if (score >= 60) level = 'high';
    else if (score >= 40) level = 'medium';
    else level = 'low';
    
    return { level, score: Math.round(score) };
  };

  const getPriorityBadge = (priorityLevel, priorityScore, deal = null) => {
    // If priority not set, calculate it from deal data
    if (!priorityLevel && deal) {
      const calculated = calculateDealPriority(deal);
      priorityLevel = calculated.level;
      priorityScore = calculated.score;
    }
    
    if (!priorityLevel) return null;
    
    const priorityConfig = {
      critical: {
        bg: '#fee2e2',
        border: '#ef4444',
        text: '#991b1b',
        label: language === 'ar' ? '🔴 حرج' : '🔴 Critical',
        icon: '#ef4444'
      },
      high: {
        bg: '#fff7ed',
        border: '#f97316',
        text: '#9a3412',
        label: language === 'ar' ? '🟠 عالي' : '🟠 High',
        icon: '#f97316'
      },
      medium: {
        bg: '#fefce8',
        border: '#eab308',
        text: '#854d0e',
        label: language === 'ar' ? '🟡 متوسط' : '🟡 Medium',
        icon: '#eab308'
      },
      low: {
        bg: '#f1f5f9',
        border: '#94a3b8',
        text: '#475569',
        label: language === 'ar' ? '⚪ منخفض' : '⚪ Low',
        icon: '#94a3b8'
      }
    };
    
    const config = priorityConfig[priorityLevel] || priorityConfig.medium;
    
    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '12px',
        background: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        {config.label}
        {priorityScore && (
          <span style={{ opacity: 0.7, fontSize: '0.7rem' }}>
            ({priorityScore})
          </span>
        )}
      </span>
    );
  };

  const handleStartRecording = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isRecording) return;
    
    try {
      setRecordingError(null);
      lastInputTextRef.current = inputText;
      audioChunksRef.current = [];
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });
      
      streamRef.current = stream;
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Convert audio chunks to blob
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        
        // Convert blob to base64
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1]; // Remove data:audio/webm;base64, prefix
          
          try {
            setIsProcessing(true);
            const response = await transcribeAudio(
              base64Audio,
              language === 'ar' ? 'ar' : 'en',
              mediaRecorder.mimeType
            );
            
            if (response.data && response.data.transcript) {
              const transcript = response.data.transcript.trim();
              setInputText(prev => {
                const newText = prev ? prev + ' ' + transcript : transcript;
                lastInputTextRef.current = newText;
                return newText;
              });
              setRecordingError(null);
            } else {
              setRecordingError(language === 'ar' 
                ? 'لم يتم الحصول على نص من التسجيل. حاول مرة أخرى.'
                : 'No transcript received from recording. Please try again.');
            }
          } catch (error) {
            console.error('Error transcribing audio:', error);
            setRecordingError(language === 'ar'
              ? 'فشل في تحويل الصوت إلى نص. يرجى المحاولة مرة أخرى أو الكتابة مباشرة.'
              : 'Failed to transcribe audio. Please try again or type directly.');
          } finally {
            setIsProcessing(false);
          }
        };
        
        reader.onerror = () => {
          console.error('Error reading audio blob');
          setRecordingError(language === 'ar'
            ? 'خطأ في معالجة التسجيل. يرجى المحاولة مرة أخرى.'
            : 'Error processing recording. Please try again.');
          setIsProcessing(false);
        };
        
        reader.readAsDataURL(audioBlob);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      setIsRecording(false);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert(language === 'ar'
          ? 'يجب السماح بالوصول إلى الميكروفون في إعدادات المتصفح.'
          : 'Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        alert(language === 'ar'
          ? 'لم يتم العثور على ميكروفون. يرجى التأكد من توصيل الميكروفون.'
          : 'Microphone not found. Please ensure a microphone is connected.');
      } else {
        alert(language === 'ar'
          ? 'حدث خطأ في بدء التسجيل. يرجى المحاولة مرة أخرى.'
          : 'Error starting recording. Please try again.');
      }
    }
  };

  const handleStopRecording = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      } catch (error) {
        console.error('Error stopping recording:', error);
        setIsRecording(false);
      }
    }
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleQuickSubmit();
    }
  };

  const handleQuickSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!inputText.trim() || isProcessing) return;
    
    setIsProcessing(true);
    try {
      // Parse the input text
      const parsed = parseDealText(inputText);
      
      // Open modal with parsed data for user to select contact and confirm
      setEditingDeal({
        ...parsed,
        isQuickAdd: true,
        notes: inputText // Keep original text as notes
      });
      setShowModal(true);
      setInputText('');
      setParsedPreview(null);
    } catch (error) {
      console.error('Error parsing deal:', error);
      alert(language === 'ar' ? 'حدث خطأ أثناء معالجة البيانات' : 'Error processing input');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="main-content">
      {/* ChatGPT-style Input Bar - More Prominent */}
      <div style={{ 
        marginBottom: '30px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fff4e6 0%, #ffe8cc 100%)',
        borderRadius: '16px',
        border: '2px solid #ffb84d',
        boxShadow: '0 4px 12px rgba(255, 184, 77, 0.15)'
      }}>
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            color: '#000000' 
          }}>
            {language === 'ar' ? 'صفقة جديدة' : 'New Deal'}
          </h2>
          <p style={{ margin: 0, fontSize: '0.95rem', color: '#666', lineHeight: '1.5' }}>
            {language === 'ar' 
              ? 'اكتب أو تحدث لوصف الصفقة وسنقوم بملء المعلومات تلقائياً. مثال: "بيع شقة في الرياض بسعر 500,000 ريال اليوم"'
              : 'Type or speak to describe the deal and we\'ll auto-fill the information. Example: "Sold apartment in Riyadh for 500,000 SAR today"'}
          </p>
        </div>
        
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          alignItems: 'flex-end',
          background: '#ffffff',
          borderRadius: '16px',
          border: '2px solid #ffd4a3',
          padding: '16px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = '#ffb84d';
          e.currentTarget.style.boxShadow = '0 0 0 4px rgba(255, 184, 77, 0.15), 0 4px 12px rgba(0,0,0,0.1)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = '#ffd4a3';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
        }}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleInputKeyPress}
              placeholder={language === 'ar' 
                ? 'اكتب هنا أو اضغط على الميكروفون للتحدث...'
                : 'Type here or click the microphone to speak...'}
              style={{
                width: '100%',
                minHeight: '80px',
                maxHeight: '250px',
                padding: '14px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1.05rem',
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
                background: 'transparent',
                lineHeight: '1.6',
                color: '#000000'
              }}
              rows={3}
            />
            
            {parsedPreview && parsedPreview.hasData && (
              <div style={{
                padding: '12px',
                background: '#e6ffe6',
                border: '1px solid #28a745',
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: '#155724'
              }}>
                <strong style={{ display: 'block', marginBottom: '6px' }}>
                  ✨ {language === 'ar' ? 'تم استخراج المعلومات:' : 'Extracted Information:'}
                </strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {parsedPreview.property_type && (
                    <span>
                      <strong>{language === 'ar' ? 'النوع:' : 'Type:'}</strong> {t[parsedPreview.property_type] || parsedPreview.property_type}
                    </span>
                  )}
                  {parsedPreview.price && (
                    <span>
                      <strong>{language === 'ar' ? 'السعر:' : 'Price:'}</strong> {parsedPreview.price}
                    </span>
                  )}
                  {parsedPreview.deal_type && (
                    <span>
                      <strong>{language === 'ar' ? 'نوع الصفقة:' : 'Deal Type:'}</strong> {t[parsedPreview.deal_type] || parsedPreview.deal_type}
                    </span>
                  )}
                  {parsedPreview.property_address && (
                    <span>
                      <strong>{language === 'ar' ? 'العنوان:' : 'Address:'}</strong> {parsedPreview.property_address}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', position: 'relative', zIndex: 10 }}>
            <button
              type="button"
              onClick={isRecording ? handleStopRecording : handleStartRecording}
              style={{
                padding: '14px',
                background: isRecording ? '#ef4444' : '#f97316',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '56px',
                height: '56px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isRecording 
                  ? '0 4px 12px rgba(239, 68, 68, 0.4)' 
                  : '0 2px 8px rgba(16, 185, 129, 0.3)',
                transform: isRecording ? 'scale(1.05)' : 'scale(1)',
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 11
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = isRecording ? 'scale(1.05)' : 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = isRecording ? 'scale(1.05)' : 'scale(1)';
              }}
              title={isRecording 
                ? (language === 'ar' ? 'إيقاف التسجيل' : 'Stop Recording')
                : (language === 'ar' ? 'ابدأ التسجيل الصوتي' : 'Start Voice Recording')}
            >
              {isRecording ? <FaStop style={{ fontSize: '18px' }} /> : <FaMicrophone style={{ fontSize: '18px' }} />}
            </button>
            
            <button
              type="button"
              onClick={handleQuickSubmit}
              disabled={!inputText.trim() || isProcessing}
              style={{
                padding: '14px 24px',
                background: inputText.trim() ? '#ffb84d' : '#e0e0e0',
                color: inputText.trim() ? '#000000' : '#999999',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: inputText.trim() && !isProcessing ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '100px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: inputText.trim() ? '0 4px 12px rgba(255, 184, 77, 0.4)' : 'none',
                transform: inputText.trim() ? 'scale(1)' : 'scale(1)',
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 11
              }}
              onMouseEnter={(e) => {
                if (inputText.trim() && !isProcessing) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 184, 77, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = inputText.trim() ? '0 4px 12px rgba(255, 184, 77, 0.4)' : 'none';
              }}
            >
              {isProcessing ? (language === 'ar' ? 'جاري المعالجة...' : 'Processing...') : (
                <>
                  <FaPlus style={{ fontSize: '14px', marginRight: '6px' }} />
                  {language === 'ar' ? 'أضف' : 'Add'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <h2 style={{ margin: 0 }}>{t.deals || 'Deals'}</h2>
            <button 
              onClick={handleAdd}
              className="btn btn-secondary"
              style={{ 
                padding: '10px 20px',
                fontSize: '0.95rem',
                fontWeight: '500',
                opacity: 0.8
              }}
              title={language === 'ar' ? 'افتح نموذج كامل للإضافة' : 'Open full form to add'}
            >
              <FaEdit style={{ fontSize: '14px', color: '#f97316' }} /> {language === 'ar' ? 'نموذج' : 'Form'}
            </button>
          </div>

          {/* Search and Filters */}
          <div className="search-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder={t.search || 'Search...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: '1', minWidth: '200px', padding: '12px 16px', fontSize: '1rem', border: '1px solid #e0e0e0', borderRadius: '6px' }}
            />
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '12px', border: '2px solid #ffb84d', borderRadius: '8px', color: '#000000' }}
            >
              <option value="">{t.allStatus || 'All Status'}</option>
              <option value="pending">{t.pending || 'Pending'}</option>
              <option value="in_progress">{t.inProgress || t.in_progress || 'In Progress'}</option>
              <option value="completed">{t.completed || 'Completed'}</option>
            </select>

            <select
              value={dealTypeFilter}
              onChange={(e) => setDealTypeFilter(e.target.value)}
              style={{ padding: '12px', border: '2px solid #ffb84d', borderRadius: '8px', color: '#000000' }}
            >
              <option value="">{t.allDealTypes || 'All Types'}</option>
              <option value="sale">{t.sale || 'Sale'}</option>
              <option value="rent">{t.rent || 'Rent'}</option>
            </select>

            <select
              value={propertyTypeFilter}
              onChange={(e) => setPropertyTypeFilter(e.target.value)}
              style={{ padding: '12px', border: '2px solid #ffb84d', borderRadius: '8px', color: '#000000' }}
            >
              <option value="">{t.allPropertyTypes || 'All Properties'}</option>
              <option value="apartment">{t.apartment || 'Apartment'}</option>
              <option value="villa">{t.villa || 'Villa'}</option>
              <option value="land">{t.land || 'Land'}</option>
              <option value="commercial">{t.commercial || 'Commercial'}</option>
              <option value="office">{t.office || 'Office'}</option>
            </select>

            <div className="view-mode-toggle" style={{ display: 'flex', border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
              <button
                onClick={() => setViewMode('cards')}
                style={{ 
                  borderRadius: 0, 
                  border: 'none',
                  borderRight: '1px solid #e0e0e0',
                  padding: '8px 14px',
                  background: viewMode === 'cards' ? '#fff4e6' : '#ffffff',
                  color: '#000000',
                  fontSize: '0.9rem',
                  fontWeight: viewMode === 'cards' ? '500' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FaTh style={{ fontSize: '14px', color: '#f97316', flexShrink: 0 }} />
                <span>{t.cards || 'Cards'}</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                style={{ 
                  borderRadius: 0, 
                  border: 'none',
                  padding: '8px 14px',
                  background: viewMode === 'table' ? '#fff4e6' : '#ffffff',
                  color: '#000000',
                  fontSize: '0.9rem',
                  fontWeight: viewMode === 'table' ? '500' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FaTable style={{ fontSize: '14px', color: '#f97316', flexShrink: 0 }} />
                <span>{t.table || 'Table'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <p>{t.loading}</p>
        </div>
      ) : deals.length === 0 ? (
        <div className="card empty-state">
          <h3>{t.noDeals || 'No deals found'}</h3>
          <p>{t.noDealsDesc || 'Click "Add Deal" to create your first deal'}</p>
        </div>
      ) : viewMode === 'table' ? (
        <div className="card" style={{ overflow: 'visible' }}>
          <div className="table-container" style={{ overflowX: 'auto', width: '100%', display: 'block' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#faf5f0', borderBottom: '2px solid #ffb84d' }}>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{t.contactName || 'Contact'}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{t.propertyType || 'Property Type'}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{t.dealType || 'Deal Type'}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{t.price || 'Price'}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{t.dealDate || 'Date'}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{language === 'ar' ? 'الأولوية' : 'Priority'}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{t.status || 'Status'}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{t.progress || (language === 'ar' ? 'التقدم' : 'Progress')}</th>
                  <th style={{ padding: '12px', textAlign: language === 'ar' ? 'right' : 'left' }}>{t.actions || 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((deal) => (
                  <tr key={deal.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {deal.contact_name || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {translatePropertyType(deal.property_type)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {translateDealType(deal.deal_type)}
                    </td>
                    <td style={{ padding: '12px', fontWeight: '600' }}>
                      {formatPrice(deal.price)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {formatDate(deal.deal_date)}
                    </td>
                    <td style={{ padding: '12px' }}>
                    {getPriorityBadge(deal.priority_level, deal.priority_score, deal) || (
                      <span style={{ opacity: 0.5, fontSize: '0.85rem' }}>-</span>
                    )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {(() => {
                        const statusMeta = getDealStatusMeta(deal.status);
                        return (
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '4px',
                            background: statusMeta.background,
                            color: statusMeta.color,
                            fontSize: '0.85rem',
                            fontWeight: '500'
                          }}>
                            {statusMeta.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={getProgressValue(deal)}
                          onChange={(e) => handleProgressChange(deal.id, e.target.value)}
                          style={{ width: '120px' }}
                        />
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={getProgressValue(deal)}
                          onChange={(e) => handleProgressChange(deal.id, e.target.value)}
                          style={{ width: '60px' }}
                        />
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleProgressSave(deal)}
                        >
                          {language === 'ar' ? 'تحديث' : 'Update'}
                        </button>
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleEdit(deal)}
                        >
                          {t.edit}
                        </button>
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleDelete(deal.id)}
                          style={{ color: '#dc3545' }}
                        >
                          {t.delete}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="deals-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
          {deals.map((deal) => (
            <div key={deal.id} className="card" style={{ 
              padding: '20px',
              background: '#ffffff',
              borderRadius: '12px',
              border: '2px solid #ffd4a3',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600' }}>
                      {deal.contact_name || '-'}
                    </h3>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '4px',
                      background: '#fff4e6',
                      color: '#000000',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}>
                      {translatePropertyType(deal.property_type)}
                    </span>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '4px',
                      background: '#e6f3ff',
                      color: '#000000',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}>
                      {translateDealType(deal.deal_type)}
                    </span>
                  </div>
                </div>
                {(() => {
                  const statusMeta = getDealStatusMeta(deal.status);
                  return (
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      background: statusMeta.background,
                      color: statusMeta.color,
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {statusMeta.label}
                    </span>
                  );
                })()}
              </div>

              {deal.property_address && (
                <p style={{ margin: '8px 0', fontSize: '0.95rem', color: '#666' }}>
                  <FaMapMarkerAlt style={{ fontSize: '14px', color: '#f97316' }} /> {deal.property_address}
                </p>
              )}

              {deal.price && (
                <p style={{ margin: '8px 0', fontSize: '1.3rem', fontWeight: 'bold', color: '#000000' }}>
                  <FaMoneyBillWave style={{ fontSize: '14px', color: '#f97316' }} /> {formatPrice(deal.price)}
                </p>
              )}

              {deal.deal_date && (
                <p style={{ margin: '8px 0', fontSize: '0.9rem', color: '#666' }}>
                  <FaCalendar style={{ fontSize: '14px', color: '#f97316' }} /> {formatDate(deal.deal_date)}
                </p>
              )}

              {/* Due Date and Priority Status - Separate Display */}
              {(() => {
                const targetDate = deal.due_date || deal.deal_date;
                if (!targetDate) return null;
                
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const due = new Date(targetDate);
                due.setHours(0, 0, 0, 0);
                const diffTime = due - today;
                const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                const priorityLevel = deal.priority_level || (deal.priority_score ? 
                  (deal.priority_score >= 80 ? 'critical' : deal.priority_score >= 60 ? 'high' : deal.priority_score >= 40 ? 'medium' : 'low') : 
                  'medium');
                
                const priorityColors = {
                  critical: { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' },
                  high: { bg: '#fff7ed', border: '#f97316', text: '#9a3412' },
                  medium: { bg: '#fefce8', border: '#eab308', text: '#854d0e' },
                  low: { bg: '#f1f5f9', border: '#94a3b8', text: '#475569' }
                };
                const colors = priorityColors[priorityLevel] || priorityColors.medium;
                
                return (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '8px', 
                    marginTop: '10px',
                    marginBottom: '10px',
                    padding: '10px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    {/* Priority Status */}
                    {priorityLevel && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#666' }}>
                          {language === 'ar' ? 'حالة الأولوية:' : 'Priority Status:'}
                        </span>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '4px',
                          background: colors.bg,
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}>
                          {priorityLevel === 'critical' && (language === 'ar' ? '🔴 حرج' : '🔴 Critical')}
                          {priorityLevel === 'high' && (language === 'ar' ? '🟠 عالي' : '🟠 High')}
                          {priorityLevel === 'medium' && (language === 'ar' ? '🟡 متوسط' : '🟡 Medium')}
                          {priorityLevel === 'low' && (language === 'ar' ? '⚪ منخفض' : '⚪ Low')}
                        </span>
                      </div>
                    )}
                    
                    {/* Time to Finish */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaClock style={{ fontSize: '14px', color: daysUntil < 0 ? '#dc2626' : daysUntil === 0 ? '#f97316' : '#6b7280' }} />
                      <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#666' }}>
                        {language === 'ar' ? 'وقت الانتهاء:' : 'Time to Finish:'}
                      </span>
                      <span style={{
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: daysUntil < 0 ? '#dc2626' : daysUntil === 0 ? '#f97316' : '#6b7280'
                      }}>
                        {daysUntil < 0 
                          ? `${language === 'ar' ? 'متأخر' : 'Overdue'} (${Math.abs(daysUntil)} ${language === 'ar' ? 'يوم' : 'days'})`
                          : daysUntil === 0
                          ? `${language === 'ar' ? 'اليوم' : 'Today'}`
                          : `${daysUntil} ${language === 'ar' ? 'أيام متبقية' : 'days left'}`
                        }
                      </span>
                    </div>
                  </div>
                );
              })()}

              {deal.notes && (
                <p style={{ margin: '12px 0 0 0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
                  {deal.notes}
                </p>
              )}

              {/* Progress Bar + Quick Select */}
              {deal.progress !== undefined && (
                <div style={{ marginTop: '15px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                      {language === 'ar' ? 'التقدم' : 'Progress'}
                    </span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#f97316' }}>
                      {getProgressValue(deal)}%
                    </span>
                  </div>
                  <div style={{ width: '100%', height: '8px', background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${getProgressValue(deal)}%`, height: '100%', background: '#f97316', transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                    {[0, 25, 50, 75, 100].map((value) => {
                      const isSelected = getProgressValue(deal) === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handleQuickProgressSet(deal, value)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '999px',
                            border: isSelected ? '2px solid #f97316' : '1px solid #e0e0e0',
                            background: isSelected ? '#fff4e6' : '#fff',
                            color: '#1a1a1a',
                            fontSize: '0.8rem',
                            fontWeight: isSelected ? '700' : '500',
                            cursor: 'pointer'
                          }}
                        >
                          {value}%
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Assigned Employees */}
              {(() => {
                const assignments = dealAssignments[deal.id] || [];
                console.log(`Deal ${deal.id} assignments check:`, {
                  hasAssignments: assignments.length > 0,
                  assignments: assignments,
                  dealAssignments: dealAssignments,
                  dealId: deal.id,
                  isOwnerOrManager: isOwnerOrManager
                });
                
                if (!isOwnerOrManager) return null;
                if (assignments.length === 0) {
                  return (
                    <div style={{ marginTop: '10px', marginBottom: '10px', padding: '8px', background: '#f9fafb', borderRadius: '6px', fontSize: '0.85rem', color: '#6b7280' }}>
                      {language === 'ar' ? 'لا يوجد موظفين معينين' : 'No employees assigned'}
                    </div>
                  );
                }
                
                return (
                  <div style={{ marginTop: '10px', marginBottom: '10px', padding: '10px', background: '#fff7ed', borderRadius: '6px', border: '1px solid #fdba74' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '5px', color: '#9a3412' }}>
                      <FaUserFriends style={{ marginRight: '5px', color: '#f97316' }} />
                      {language === 'ar' ? 'الموظفون المعينون' : 'Assigned Employees'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {assignments.map((assignment) => (
                        <span
                          key={assignment.employee_id || assignment.id}
                          style={{
                            padding: '4px 8px',
                            background: '#f97316',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}
                        >
                          {assignment.employee_name || assignment.name || `Employee ${assignment.employee_id}`}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div style={{ display: 'flex', gap: '8px', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                {isOwnerOrManager && (
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => handleAssignClick(deal)}
                    style={{ flex: 1, background: '#f97316', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                  >
                    <FaUserFriends />
                    {language === 'ar' ? 'تعيين' : 'Assign'}
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => handleEdit(deal)}
                  style={{ flex: 1 }}
                >
                  {t.edit}
                </button>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => handleDelete(deal.id)}
                  style={{ flex: 1, color: '#dc3545' }}
                >
                  {t.delete}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <DealModal
          deal={editingDeal}
          onClose={handleModalClose}
          language={language}
        />
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && assigningDeal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowAssignmentModal(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px' }}>
              {language === 'ar' ? 'تعيين موظفين للصفقة' : 'Assign Employees to Deal'}
            </h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              {assigningDeal.contact_name} - {assigningDeal.property_address || ''}
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                {language === 'ar' ? 'اختر الموظفين' : 'Select Employees'}
              </label>
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '2px solid #e0e0e0', borderRadius: '6px', padding: '10px' }}>
                {employees.filter(emp => emp.role === 'employee').map((employee) => (
                  <label
                    key={employee.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      marginBottom: '8px',
                      background: selectedEmployees.includes(employee.id) ? '#e0e7ff' : '#f5f5f5',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedEmployees([...selectedEmployees, employee.id]);
                        } else {
                          setSelectedEmployees(selectedEmployees.filter(id => id !== employee.id));
                        }
                      }}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: '500' }}>{employee.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>{employee.phone_number}</div>
                    </div>
                  </label>
                ))}
                {employees.filter(emp => emp.role === 'employee').length === 0 && (
                  <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                    {language === 'ar' ? 'لا يوجد موظفين' : 'No employees available'}
                  </p>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAssignmentModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#e0e0e0',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={handleAssignSubmit}
                style={{
                  padding: '10px 20px',
                  background: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FaCheckCircle />
                {language === 'ar' ? 'حفظ' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deals;

